import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calculator, TrendingDown } from 'lucide-react';
import { discountAPI } from '../api';

const BulkDiscountCalculator = ({ quantity, unitPrice, onDiscountCalculated }) => {
  const [discountInfo, setDiscountInfo] = useState(null);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    // Fetch discount tiers
    const fetchTiers = async () => {
      try {
        const tiersData = await discountAPI.getTiers();
        setTiers(tiersData);
      } catch (error) {
        console.error('Error fetching discount tiers:', error);
      }
    };
    fetchTiers();
  }, []);

  useEffect(() => {
    // Calculate discount when quantity or price changes
    const calculateDiscount = async () => {
      if (quantity > 0 && unitPrice > 0) {
        try {
          const result = await discountAPI.calculate(quantity, unitPrice);
          setDiscountInfo(result);
          if (onDiscountCalculated) {
            onDiscountCalculated(result);
          }
        } catch (error) {
          console.error('Error calculating discount:', error);
        }
      }
    };
    calculateDiscount();
  }, [quantity, unitPrice, onDiscountCalculated]);

  if (!discountInfo) return null;

  const hasDiscount = discountInfo.discount_percentage > 0;

  return (
    <Card className={hasDiscount ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="h-5 w-5 text-orange-600" />
          Bulk Discount Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Discount Tiers */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Available Discounts:</p>
          <div className="space-y-2">
            {tiers.map((tier, index) => {
              const isActive = quantity >= tier.min_quantity;
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    isActive
                      ? 'bg-green-100 border border-green-300'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isActive && <TrendingDown className="h-4 w-4 text-green-600" />}
                    <span className={`text-sm ${isActive ? 'font-semibold text-green-700' : 'text-gray-600'}`}>
                      {tier.min_quantity}+ items
                    </span>
                  </div>
                  <Badge className={isActive ? 'bg-green-600' : 'bg-gray-400'}>
                    {tier.discount_percentage}% OFF
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Order Summary */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Quantity</span>
            <span className="font-semibold text-gray-900">{discountInfo.quantity} items</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Unit Price</span>
            <span className="font-semibold text-gray-900">KES {discountInfo.unit_price.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-semibold text-gray-900">KES {discountInfo.subtotal.toLocaleString()}</span>
          </div>
          
          {hasDiscount && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-green-600 font-semibold flex items-center gap-1">
                  <TrendingDown className="h-4 w-4" />
                  Discount ({discountInfo.discount_percentage}%)
                </span>
                <span className="font-semibold text-green-600">
                  - KES {discountInfo.discount_amount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-base font-bold border-t pt-2">
                <span className="text-gray-900">Final Price</span>
                <span className="text-green-600">KES {discountInfo.final_price.toLocaleString()}</span>
              </div>
              <div className="bg-green-600 text-white rounded-lg p-3 text-center">
                <p className="text-sm font-semibold">
                  🎉 You Save KES {discountInfo.savings.toLocaleString()}!
                </p>
              </div>
            </>
          )}

          {!hasDiscount && quantity < 10 && (
            <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Order {10 - quantity} more items to unlock 5% discount!
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkDiscountCalculator;
